import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchEconomicCalendar,
  getMarketContentLanguage,
  marketDataConfig,
  type EconomicCalendarItem,
} from '../../services/marketDataApi';

type DateFilter = 'ALL' | string;
type ImpactFilter = 'ALL' | 'HIGH' | 'MEDIUM' | 'LOW';

const localeByLanguage: Record<string, string> = {
  hk: 'zh-HK',
  cn: 'zh-CN',
  en: 'en-GB',
  jp: 'ja-JP',
  kr: 'ko-KR',
  th: 'th-TH',
  vn: 'vi-VN',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
};

const getLocale = (language: string) => localeByLanguage[language.split('-')[0]] || 'en-GB';

const localDateKey = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: marketDataConfig.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const addDays = (dateKey: string, days: number) => {
  const [yearText = '1970', monthText = '01', dayText = '01'] = dateKey.split('-');
  const date = new Date(
    Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText) + days, 12),
  );
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
};

const dateKeyToUtcDate = (dateKey: string) => {
  const [yearText = '1970', monthText = '01', dayText = '01'] = dateKey.split('-');
  return new Date(Date.UTC(Number(yearText), Number(monthText) - 1, Number(dayText), 12));
};

const eventDateKey = (event: EconomicCalendarItem) => {
  const source = event.eventTimeUtc || event.eventTime;
  const date = new Date(source);
  return Number.isNaN(date.getTime()) ? '' : localDateKey(date);
};

const eventTimeLabel = (event: EconomicCalendarItem, locale: string) => {
  const source = event.eventTimeUtc || event.eventTime;
  const date = new Date(source);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    timeZone: marketDataConfig.timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const countryFlag = (countryCode?: string | null) => {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '●';
  return String.fromCodePoint(...[...code].map((character) => 127397 + character.charCodeAt(0)));
};

const normalizedImpact = (event: EconomicCalendarItem): Exclude<ImpactFilter, 'ALL'> => {
  const impact = String(event.impact || '').trim().toUpperCase();
  if (impact === 'HIGH') return 'HIGH';
  if (impact === 'MEDIUM') return 'MEDIUM';
  if (impact === 'LOW') return 'LOW';

  const importance = Number(event.importance);
  if (Number.isFinite(importance)) {
    if (importance >= 3) return 'HIGH';
    if (importance >= 2) return 'MEDIUM';
  }

  return 'LOW';
};

const impactDotClass = (impact: Exclude<ImpactFilter, 'ALL'>) => {
  if (impact === 'HIGH') return 'bg-red-500';
  if (impact === 'MEDIUM') return 'bg-orange-500';
  return 'bg-yellow-400';
};

const impactBadgeClass = (impact: Exclude<ImpactFilter, 'ALL'>) => {
  if (impact === 'HIGH') return 'border-red-200 bg-red-50 text-red-700';
  if (impact === 'MEDIUM') return 'border-orange-200 bg-orange-50 text-orange-700';
  return 'border-yellow-200 bg-yellow-50 text-yellow-700';
};

const formatValue = (value: unknown, unit?: string | null) => {
  if (value === null || value === undefined || value === '') return '—';

  const numeric = Number(value);
  const formatted = Number.isFinite(numeric)
    ? new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4 }).format(numeric)
    : String(value);
  const normalizedUnit = String(unit || '').trim();

  if (!normalizedUnit) return formatted;
  if (['%', 'bps', '°'].includes(normalizedUnit)) return `${formatted}${normalizedUnit}`;
  return `${formatted} ${normalizedUnit}`;
};

const isReleased = (event: EconomicCalendarItem) =>
  event.actual !== null && event.actual !== undefined && event.actual !== '';

const sortEvents = (items: EconomicCalendarItem[]) =>
  [...items].sort((left, right) => {
    const leftTime = new Date(left.eventTimeUtc || left.eventTime).getTime();
    const rightTime = new Date(right.eventTimeUtc || right.eventTime).getTime();
    return leftTime - rightTime;
  });

export default function EconomicCalendarExplorer() {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const apiLanguage = getMarketContentLanguage(i18n.resolvedLanguage || i18n.language);
  const today = useMemo(() => localDateKey(new Date()), []);
  const [anchorDate, setAnchorDate] = useState(today);
  const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');
  const [impactFilter, setImpactFilter] = useState<ImpactFilter>('ALL');
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState<EconomicCalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const rangeStart = useMemo(() => addDays(anchorDate, -3), [anchorDate]);
  const rangeEndExclusive = useMemo(() => addDays(anchorDate, 4), [anchorDate]);
  const dateKeys = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(rangeStart, index)),
    [rangeStart],
  );

  useEffect(() => {
    setDateFilter('ALL');
  }, [rangeStart]);

  useEffect(() => {
    let mounted = true;
    let activeController: AbortController | null = null;

    const load = async (initial: boolean) => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      if (initial) setLoading(true);
      else setRefreshing(true);
      setError('');

      try {
        const response = await fetchEconomicCalendar(
          rangeStart,
          rangeEndExclusive,
          controller.signal,
          { limit: 500, language: apiLanguage },
        );
        if (!mounted) return;

        const uniqueItems = Array.from(
          new Map(response.items.map((event) => [event.id, event])).values(),
        );
        setItems(sortEvents(uniqueItems));
        setLastUpdated(new Date());
      } catch (requestError) {
        if (!mounted || controller.signal.aborted) return;
        setError(
          requestError instanceof Error
            ? requestError.message
            : t('calendar.loadError', {
                defaultValue: 'The economic calendar is temporarily unavailable.',
              }),
        );
      } finally {
        if (!mounted) return;
        setLoading(false);
        setRefreshing(false);
      }
    };

    void load(true);
    const timer = window.setInterval(
      () => void load(false),
      marketDataConfig.contentRefreshMs,
    );

    return () => {
      mounted = false;
      activeController?.abort();
      window.clearInterval(timer);
    };
  }, [apiLanguage, rangeStart, rangeEndExclusive, refreshToken, t]);

  const filteredItems = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();

    return items.filter((event) => {
      const dateMatches = dateFilter === 'ALL' || eventDateKey(event) === dateFilter;
      if (!dateMatches) return false;

      const impactMatches =
        impactFilter === 'ALL' || normalizedImpact(event) === impactFilter;
      if (!impactMatches) return false;

      if (!normalizedKeyword) return true;
      return [event.event, event.currency, event.countryCode, event.unit]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedKeyword);
    });
  }, [dateFilter, impactFilter, items, keyword]);

  const groupedItems = useMemo(
    () =>
      dateKeys
        .map((dateKey) => ({
          dateKey,
          items: filteredItems.filter((event) => eventDateKey(event) === dateKey),
        }))
        .filter((group) =>
          dateFilter === 'ALL' ? group.items.length > 0 : group.dateKey === dateFilter,
        ),
    [dateFilter, dateKeys, filteredItems],
  );

  const dateLabel = (dateKey: string, compact = false) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: compact ? 'short' : 'long',
      month: compact ? 'short' : 'long',
      day: 'numeric',
    }).format(dateKeyToUtcDate(dateKey));

  const shortDateLabel = (dateKey: string) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
    }).format(dateKeyToUtcDate(dateKey));

  const rangeLabel = `${shortDateLabel(rangeStart)} – ${shortDateLabel(addDays(rangeEndExclusive, -1))}`;

  const impactFilters: { key: ImpactFilter; label: string }[] = [
    {
      key: 'ALL',
      label: t('calendar.impactAll', { defaultValue: 'All impact' }),
    },
    {
      key: 'HIGH',
      label: t('calendar.impactHigh', { defaultValue: 'High' }),
    },
    {
      key: 'MEDIUM',
      label: t('calendar.impactMedium', { defaultValue: 'Medium' }),
    },
    {
      key: 'LOW',
      label: t('calendar.impactLow', { defaultValue: 'Low' }),
    },
  ];

  return (
    <section id="economic-calendar" className="scroll-mt-28">
      <div className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-800 px-5 py-6 text-white md:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-500/15 text-2xl text-yellow-400">
                  <i className="ri-calendar-event-line" />
                </span>
                <div>
                  <h2 className="text-2xl font-bold md:text-3xl">{t('home.ecoCal')}</h2>
                  <p className="mt-1 text-sm text-gray-300">
                    {rangeLabel} · {t('calendar.hongKongTime', { defaultValue: 'Hong Kong time (GMT+8)' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setAnchorDate((value) => addDays(value, -7))}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 text-sm font-semibold text-white transition hover:border-yellow-400 hover:text-yellow-300"
                aria-label={t('calendar.previousDays', { defaultValue: 'Previous 7 days' })}
              >
                <i className="ri-arrow-left-s-line text-lg" />
                <span className="hidden sm:inline">
                  {t('calendar.previousDays', { defaultValue: 'Previous 7 days' })}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setAnchorDate(today)}
                className="h-10 rounded-xl bg-yellow-500 px-4 text-sm font-bold text-gray-950 transition hover:bg-yellow-400"
              >
                {t('calendar.today', { defaultValue: 'Today' })}
              </button>

              <label className="relative">
                <span className="sr-only">
                  {t('calendar.jumpToDate', { defaultValue: 'Jump to date' })}
                </span>
                <input
                  type="date"
                  value={anchorDate}
                  onChange={(event) => {
                    if (event.target.value) setAnchorDate(event.target.value);
                  }}
                  className="h-10 rounded-xl border border-white/15 bg-white/10 px-3 text-sm font-semibold text-white outline-none [color-scheme:dark] focus:border-yellow-400"
                />
              </label>

              <button
                type="button"
                onClick={() => setAnchorDate((value) => addDays(value, 7))}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 text-sm font-semibold text-white transition hover:border-yellow-400 hover:text-yellow-300"
                aria-label={t('calendar.nextDays', { defaultValue: 'Next 7 days' })}
              >
                <span className="hidden sm:inline">
                  {t('calendar.nextDays', { defaultValue: 'Next 7 days' })}
                </span>
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-5 overflow-x-auto pb-1">
            <div className="flex min-w-max gap-2">
              <button
                type="button"
                onClick={() => setDateFilter('ALL')}
                className={`rounded-2xl border px-4 py-3 text-left transition ${
                  dateFilter === 'ALL'
                    ? 'border-yellow-500 bg-yellow-500 text-gray-950 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-400'
                }`}
              >
                <span className="block text-xs font-semibold uppercase tracking-wide opacity-75">
                  {t('calendar.completeView', { defaultValue: 'Complete view' })}
                </span>
                <span className="mt-0.5 block text-sm font-bold">
                  {t('calendar.allDays', { defaultValue: 'All 7 days' })}
                </span>
              </button>

              {dateKeys.map((dateKey) => {
                const count = items.filter((event) => eventDateKey(event) === dateKey).length;
                const selected = dateFilter === dateKey;
                const isToday = dateKey === today;

                return (
                  <button
                    key={dateKey}
                    type="button"
                    onClick={() => setDateFilter(dateKey)}
                    className={`min-w-[112px] rounded-2xl border px-4 py-3 text-left transition ${
                      selected
                        ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-400'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide opacity-75">
                      {new Intl.DateTimeFormat(locale, {
                        timeZone: 'UTC',
                        weekday: 'short',
                      }).format(dateKeyToUtcDate(dateKey))}
                      {isToday && (
                        <span className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[10px] font-bold text-gray-950">
                          {t('calendar.today', { defaultValue: 'Today' })}
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-sm font-bold">{shortDateLabel(dateKey)}</span>
                    <span className="mt-1 block text-xs opacity-70">
                      {count} {t('calendar.events', { defaultValue: 'events' })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {impactFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setImpactFilter(filter.key)}
                  className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition ${
                    impactFilter === filter.key
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-400'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
              <label className="relative block flex-1 lg:w-80">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder={t('calendar.searchPlaceholder', {
                    defaultValue: 'Search event, country or currency',
                  })}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm outline-none transition focus:border-yellow-500"
                />
              </label>

              <button
                type="button"
                onClick={() => {
                  setRefreshing(true);
                  setRefreshToken((value) => value + 1);
                }}
                disabled={refreshing}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={`ri-refresh-line ${refreshing ? 'animate-spin' : ''}`} />
                {t('home.refresh')}
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-col gap-2 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {filteredItems.length} {t('calendar.eventsFound', { defaultValue: 'events shown' })}
            </span>
            <span>
              {t('calendar.lastUpdated', { defaultValue: 'Last updated' })}:{' '}
              {lastUpdated
                ? new Intl.DateTimeFormat(locale, {
                    timeZone: marketDataConfig.timeZone,
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }).format(lastUpdated)
                : '—'}
            </span>
          </div>

          {error && (
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line mt-0.5 text-lg" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 8 }, (_, index) => (
                <div key={index} className="animate-pulse rounded-2xl border border-gray-100 p-5">
                  <div className="mb-3 h-4 w-1/3 rounded bg-gray-100" />
                  <div className="h-4 w-4/5 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : groupedItems.length === 0 || filteredItems.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center text-gray-500">
              <i className="ri-calendar-close-line mb-3 block text-5xl text-gray-300" />
              <p className="font-semibold text-gray-700">
                {t('calendar.noEvents', { defaultValue: 'No economic events match this view.' })}
              </p>
              <p className="mt-2 text-sm">
                {t('calendar.tryAnotherDate', {
                  defaultValue: 'Choose another day or clear the filters.',
                })}
              </p>
            </div>
          ) : (
            <div className="space-y-7">
              {groupedItems.map((group) => (
                <section key={group.dateKey}>
                  <div className="mb-3 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{dateLabel(group.dateKey)}</h3>
                      <p className="text-xs text-gray-500">
                        {group.items.length} {t('calendar.events', { defaultValue: 'events' })}
                      </p>
                    </div>
                    {group.dateKey === today && (
                      <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-bold text-yellow-800">
                        {t('calendar.today', { defaultValue: 'Today' })}
                      </span>
                    )}
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                    <div className="hidden grid-cols-[80px_105px_minmax(220px,1fr)_90px_115px_115px_115px] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500 lg:grid">
                      <span>{t('calendar.time', { defaultValue: 'Time' })}</span>
                      <span>{t('home.currency')}</span>
                      <span>{t('home.events')}</span>
                      <span>{t('calendar.impact', { defaultValue: 'Impact' })}</span>
                      <span>{t('home.previous')}</span>
                      <span>{t('home.forecast')}</span>
                      <span>{t('home.actual')}</span>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {group.items.map((event) => {
                        const impact = normalizedImpact(event);
                        const released = isReleased(event);

                        return (
                          <article
                            key={event.id}
                            className="grid gap-4 px-4 py-5 transition hover:bg-gray-50 lg:grid-cols-[80px_105px_minmax(220px,1fr)_90px_115px_115px_115px] lg:items-center lg:gap-3 lg:py-4"
                          >
                            <div className="flex items-center justify-between lg:block">
                              <span className="text-xs font-semibold uppercase text-gray-400 lg:hidden">
                                {t('calendar.time', { defaultValue: 'Time' })}
                              </span>
                              <span className="text-sm font-bold text-gray-900">
                                {eventTimeLabel(event, locale)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between gap-3 lg:justify-start">
                              <span className="text-xs font-semibold uppercase text-gray-400 lg:hidden">
                                {t('home.currency')}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-xl" aria-hidden="true">
                                  {countryFlag(event.countryCode)}
                                </span>
                                <div>
                                  <div className="text-sm font-bold text-gray-900">
                                    {event.currency || event.countryCode || '—'}
                                  </div>
                                  {event.countryCode && event.currency && (
                                    <div className="text-[11px] text-gray-400">{event.countryCode}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-sm font-semibold leading-6 text-gray-900">
                                  {event.event}
                                </h4>
                                <span
                                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                                    released
                                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                      : 'border-blue-200 bg-blue-50 text-blue-700'
                                  }`}
                                >
                                  {released
                                    ? t('calendar.released', { defaultValue: 'Released' })
                                    : t('calendar.pending', { defaultValue: 'Pending' })}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between lg:block">
                              <span className="text-xs font-semibold uppercase text-gray-400 lg:hidden">
                                {t('calendar.impact', { defaultValue: 'Impact' })}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${impactBadgeClass(
                                  impact,
                                )}`}
                              >
                                <span className={`h-2 w-2 rounded-full ${impactDotClass(impact)}`} />
                                {t(`calendar.impact${impact.charAt(0)}${impact.slice(1).toLowerCase()}`, {
                                  defaultValue:
                                    impact.charAt(0) + impact.slice(1).toLowerCase(),
                                })}
                              </span>
                            </div>

                            <div className="grid grid-cols-3 gap-2 lg:contents">
                              <div className="rounded-xl bg-gray-50 p-3 lg:rounded-none lg:bg-transparent lg:p-0">
                                <span className="block text-[11px] font-semibold uppercase text-gray-400 lg:hidden">
                                  {t('home.previous')}
                                </span>
                                <span className="mt-1 block text-sm font-semibold text-gray-700 lg:mt-0">
                                  {formatValue(event.previous, event.unit)}
                                </span>
                              </div>
                              <div className="rounded-xl bg-gray-50 p-3 lg:rounded-none lg:bg-transparent lg:p-0">
                                <span className="block text-[11px] font-semibold uppercase text-gray-400 lg:hidden">
                                  {t('home.forecast')}
                                </span>
                                <span className="mt-1 block text-sm font-semibold text-gray-700 lg:mt-0">
                                  {formatValue(event.estimate, event.unit)}
                                </span>
                              </div>
                              <div className="rounded-xl bg-gray-50 p-3 lg:rounded-none lg:bg-transparent lg:p-0">
                                <span className="block text-[11px] font-semibold uppercase text-gray-400 lg:hidden">
                                  {t('home.actual')}
                                </span>
                                <span
                                  className={`mt-1 block text-sm font-bold lg:mt-0 ${
                                    released ? 'text-gray-950' : 'text-blue-600'
                                  }`}
                                >
                                  {formatValue(event.actual, event.unit)}
                                </span>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="mt-5 text-center text-xs leading-5 text-gray-500">
        {t('calendar.disclaimer', {
          defaultValue:
            'Calendar times use Hong Kong time. Figures may be revised and are provided for information only.',
        })}
      </p>
    </section>
  );
}
